import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiEdit, FiTrash2, FiPlus, FiSettings, FiClock, FiX, FiCheck, FiAlertCircle, FiUpload, FiImage } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';
import { getImageUrl, getPlaceholderUrl } from '../../utils/imageUrl';

// Debug function to log image URL details
const debugImageUrl = (serviceId, imageUrl) => {
  const fullUrl = getImageUrl(imageUrl);
  console.log(`Service ${serviceId} image debug:`, {
    original: imageUrl,
    processed: fullUrl
  });
  return fullUrl;
};

const AdminServices = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [categories, setCategories] = useState([
    'Perawatan Rutin',
    'Rem & Suspensi',
    'AC & Electrical',
    'Mesin'
  ]);

  // Form state for add/edit service
  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    price: '',
    category: 'Perawatan Rutin',
    estimated_time: '',
    in_stock: true,
    featured: false,
    stock: 0,
    image: null,
    imagePreview: null
  });

  // Form validation state
  const [validation, setValidation] = useState({
    name: true,
    price: true,
    estimated_time: true,
    category: true,
    stock: true
  });

  const handleEditService = useCallback((service) => {
    prepareFormForEdit(service);
    setModalType('edit');
    setSelectedService(service);
    setIsModalOpen(true);
  }, []);

  const fetchServiceForEdit = useCallback(async (serviceId) => {
    try {
      const response = await API.get(`/api/services/${serviceId}`);
      if (response.data) {
        handleEditService(response.data);
      }
    } catch (error) {
      console.error('Error fetching service for edit:', error);
      alert('Gagal memuat layanan untuk diedit');
    }
  }, [handleEditService]);

  useEffect(() => {
    // Fetch service categories from backend
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await API.get('/api/services/categories/all');
      if (Array.isArray(response.data) && response.data.length > 0) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error fetching service categories:', error);
      // Keep default categories if fetch fails
    }
  };

  // Form handling functions
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setServiceForm({
      ...serviceForm,
      [name]: type === 'checkbox' ? checked : value
    });

    // Clear validation error when user types
    if (['name', 'price', 'estimated_time', 'category', 'stock'].includes(name) && validation[name] === false) {
      setValidation({
        ...validation,
        [name]: true
      });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setServiceForm({
        ...serviceForm,
        image: file,
        imagePreview: URL.createObjectURL(file)
      });
    }
  };

  const validateForm = () => {
    const newValidation = {
      name: serviceForm.name.trim() !== '',
      price: serviceForm.price > 0,
      estimated_time: serviceForm.estimated_time > 0,
      category: serviceForm.category.trim() !== '',
      stock: serviceForm.stock >= 0
    };

    setValidation(newValidation);
    return Object.values(newValidation).every(valid => valid);
  };

  const resetForm = () => {
    setServiceForm({
      name: '',
      description: '',
      price: '',
      category: 'Perawatan Rutin',
      estimated_time: '',
      in_stock: true,
      featured: false,
      stock: 0,
      image: null,
      imagePreview: null
    });
    setValidation({
      name: true,
      price: true,
      estimated_time: true,
      category: true,
      stock: true
    });
    setFormError('');
  };

  const prepareFormForEdit = (service) => {
    console.log('Preparing service for edit:', service);
    setServiceForm({
      name: service.name || '',
      description: service.description || '',
      price: service.price || '',
      category: service.category || 'Perawatan Rutin',
      estimated_time: service.estimated_time || '',
      in_stock: typeof service.in_stock === 'boolean' ? service.in_stock : true,
      featured: typeof service.featured === 'boolean' ? service.featured : false,
      stock: service.stock || 0,
      image: null,
      imagePreview: service.image_url ? getImageUrl(service.image_url) : null
    });

    // Log the form state after preparation
    console.log('Service form after preparation:', {
      ...service,
      imagePreview: service.image_url ? getImageUrl(service.image_url) : null
    });
  };

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/services', {
        params: {
          page: currentPage,
          category: filterCategory !== 'all' ? filterCategory : undefined,
          search: searchTerm || undefined,
        },
      });

      // Add debug logging for received services
      console.log('Fetched services:', response.data);

      // Check for service with images
      const servicesWithImages = response.data.filter(service => service.image_url);
      console.log('Services with images:', servicesWithImages.length);
      if (servicesWithImages.length > 0) {
        console.log('Sample image URL:', servicesWithImages[0].image_url);
        // Test image URL generation
        const sampleService = servicesWithImages[0];
        debugImageUrl(sampleService.id, sampleService.image_url);
      }

      setServices(response.data);
      setTotalPages(Math.ceil(response.data.length / 10)); // Assuming 10 items per page
    } catch (error) {
      console.error('Error fetching services:', error);
      setServices([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterCategory, searchTerm]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  useEffect(() => {
    if (location.state?.editServiceId) {
      const serviceId = location.state.editServiceId;
      const serviceToEdit = services.find(s => s.id === serviceId);

      if (serviceToEdit) {
        handleEditService(serviceToEdit);
      } else {
        fetchServiceForEdit(serviceId);
      }

      window.history.replaceState({}, document.title);
    }
  }, [fetchServiceForEdit, handleEditService, location.state, services]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchServices();
  };

  const handleCategoryChange = (e) => {
    setFilterCategory(e.target.value);
    setCurrentPage(1);
  };

  const handleAddService = () => {
    resetForm(); // Reset form before opening
    setModalType('add');
    setSelectedService(null);
    setIsModalOpen(true);
  };

  const handleDeleteService = (service) => {
    setModalType('delete');
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
    // Reset form when closing modal
    resetForm();
  };

  const confirmDelete = async () => {
    try {
      await API.delete(`/api/services/${selectedService.id}`);
      fetchServices();
      closeModal();
      alert('Layanan berhasil dihapus!');
    } catch (error) {
      console.error('Error deleting service:', error);
      alert('Gagal menghapus layanan: ' + (error.response?.data?.message || error.message));
      closeModal();
      fetchServices();
    }
  };

  const handleSubmitService = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setFormSubmitting(true);
    setFormError('');

    try {
      // Create FormData object for both operations to maintain consistent API
      const formData = new FormData();
      formData.append('name', serviceForm.name);
      formData.append('description', serviceForm.description || '');
      formData.append('price', serviceForm.price);
      formData.append('category', serviceForm.category);
      formData.append('estimated_time', serviceForm.estimated_time);
      formData.append('in_stock', serviceForm.in_stock);
      formData.append('featured', serviceForm.featured);
      formData.append('stock', serviceForm.stock);

      // Add image if available
      if (serviceForm.image) {
        formData.append('image', serviceForm.image);
      }

      let response;

      if (modalType === 'add') {
        // Create new service with FormData
        response = await API.post('/api/services', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        console.log('Service created:', response.data);
      } else {
        // For update, we need to send FormData as well since backend expects it
        console.log('Updating service with ID:', selectedService.id);
        console.log('Update data:', Object.fromEntries(formData.entries()));

        response = await API.put(`/api/services/${selectedService.id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        console.log('Service updated:', response.data);
      }

      // Make sure location state is cleared to prevent modal from reopening
      if (location.state?.editServiceId) {
        window.history.replaceState({}, document.title);
      }

      // Close the modal and reset form first
      closeModal();

      // Then refresh the service list
      fetchServices();

      // Show success message
      alert(
        modalType === 'add'
          ? 'Layanan berhasil ditambahkan!'
          : 'Layanan berhasil diperbarui!'
      );
    } catch (error) {
      console.error('Error submitting service:', error);
      setFormError(
        error.response?.data?.message ||
        'Terjadi kesalahan saat menyimpan data layanan'
      );
    } finally {
      setFormSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return (
        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
          Aktif
        </span>
      );
    } else {
      return (
        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-semibold">
          Tidak Aktif
        </span>
      );
    }
  };

  const renderPagination = () => {
    return (
      <div className="flex justify-center mt-6">
        <nav className="flex items-center">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Sebelumnya
          </button>
          <div className="px-4 py-1 border-t border-b border-gray-300 bg-white text-sm text-gray-700">
            Halaman {currentPage} dari {totalPages}
          </div>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Berikutnya
          </button>
        </nav>
      </div>
    );
  };

  const renderDeleteModal = () => {
    if (!selectedService) return null;

    return (
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold text-secondary mb-4">Hapus Layanan</h2>
        <p className="mb-6">
          Apakah Anda yakin ingin menghapus layanan <strong>{selectedService.name}</strong>?
        </p>
        <div className="flex justify-end">
          <button
            onClick={closeModal}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 mr-2"
          >
            Batal
          </button>
          <button
            onClick={confirmDelete}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Hapus
          </button>
        </div>
      </div>
    );
  };

  const renderServiceForm = () => {
    const isAddMode = modalType === 'add';
    const title = isAddMode ? 'Tambah Layanan Baru' : 'Edit Layanan';
    const submitText = isAddMode ? 'Tambah' : 'Simpan Perubahan';

    return (
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full overflow-y-auto max-h-[90vh]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-secondary">{title}</h2>
          <button
            onClick={closeModal}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <FiX size={24} />
          </button>
        </div>

        {formError && (
          <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <div className="flex items-center">
              <FiAlertCircle className="mr-2" />
              <span>{formError}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmitService} className="space-y-4">
          {/* Service Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Nama Layanan <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={serviceForm.name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border ${validation.name ? 'border-gray-300' : 'border-red-500'
                } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
              required
            />
            {!validation.name && (
              <p className="mt-1 text-sm text-red-500">Nama layanan wajib diisi</p>
            )}
          </div>

          {/* Two column layout for compact form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left column */}
            <div className="space-y-4">
              {/* Category */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  Kategori <span className="text-red-500">*</span>
                </label>
                <select
                  id="category"
                  name="category"
                  value={serviceForm.category}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${validation.category ? 'border-gray-300' : 'border-red-500'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  required
                >
                  {categories.map((category, index) => (
                    <option key={index} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                {!validation.category && (
                  <p className="mt-1 text-sm text-red-500">Kategori wajib dipilih</p>
                )}
              </div>

              {/* Price */}
              <div>
                <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                  Harga (Rp) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  min="0"
                  value={serviceForm.price}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${validation.price ? 'border-gray-300' : 'border-red-500'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  required
                />
                {!validation.price && (
                  <p className="mt-1 text-sm text-red-500">Harga wajib diisi</p>
                )}
              </div>

              {/* Stock */}
              <div>
                <label htmlFor="stock" className="block text-sm font-medium text-gray-700 mb-1">
                  Stok <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="stock"
                  name="stock"
                  min="0"
                  value={serviceForm.stock}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${validation.stock ? 'border-gray-300' : 'border-red-500'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  required
                />
                {!validation.stock && (
                  <p className="mt-1 text-sm text-red-500">Stok tidak boleh negatif</p>
                )}
              </div>
            </div>

            {/* Right column */}
            <div className="space-y-4">
              {/* Estimated Time */}
              <div>
                <label htmlFor="estimated_time" className="block text-sm font-medium text-gray-700 mb-1">
                  Estimasi Waktu (menit) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="estimated_time"
                  name="estimated_time"
                  min="0"
                  value={serviceForm.estimated_time}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${validation.estimated_time ? 'border-gray-300' : 'border-red-500'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  required
                />
                {!validation.estimated_time && (
                  <p className="mt-1 text-sm text-red-500">Estimasi waktu wajib diisi</p>
                )}
              </div>

              {/* Status Options */}
              <div className="space-y-2 mt-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="in_stock"
                    name="in_stock"
                    checked={serviceForm.in_stock}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="in_stock" className="ml-2 block text-sm text-gray-700">
                    Tersedia (In Stock)
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="featured"
                    name="featured"
                    checked={serviceForm.featured}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="featured" className="ml-2 block text-sm text-gray-700">
                    Fitur di Halaman Utama
                  </label>
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gambar Layanan
                </label>
                <div className="mt-1 flex items-center">
                  {serviceForm.imagePreview ? (
                    <div className="relative h-24 w-24 rounded overflow-hidden">
                      <img
                        src={serviceForm.imagePreview}
                        alt="Preview"
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setServiceForm({
                          ...serviceForm,
                          image: null,
                          imagePreview: null
                        })}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                        title="Remove image"
                      >
                        <FiX size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="h-24 w-24 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center">
                      <FiImage className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  <label htmlFor="image" className="ml-5 bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary cursor-pointer">
                    <FiUpload className="inline-block mr-1" />
                    {serviceForm.imagePreview ? 'Ganti Gambar' : 'Upload Gambar'}
                  </label>
                  <input
                    id="image"
                    name="image"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="sr-only"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Description - full width */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Deskripsi
            </label>
            <textarea
              id="description"
              name="description"
              rows="3"
              value={serviceForm.description}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="flex justify-end pt-4 border-t">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 mr-2"
              disabled={formSubmitting}
            >
              Batal
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark flex items-center"
              disabled={formSubmitting}
            >
              {formSubmitting ? (
                <>
                  <div className="animate-spin h-4 w-4 mr-2 border-t-2 border-b-2 border-white rounded-full"></div>
                  <span>Menyimpan...</span>
                </>
              ) : (
                <>
                  <FiCheck className="mr-2" />
                  <span>{submitText}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <DashboardLayout userRole="admin" userName="Admin">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Manajemen Layanan</h1>
          <p className="text-gray-600">Kelola semua layanan bengkel</p>
        </div>
        <button
          onClick={handleAddService}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center"
        >
          <FiPlus className="mr-2" /> Tambah Layanan
        </button>
      </div>

      {/* Search and Filter */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <form onSubmit={handleSearch} className="flex mb-4 md:mb-0">
            <div className="relative">
              <input
                type="text"
                placeholder="Cari nama layanan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-l focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <button
              type="submit"
              className="bg-primary text-white px-4 py-2 rounded-r hover:bg-primary-dark"
            >
              Cari
            </button>
          </form>

          <div className="flex items-center">
            <span className="mr-2 text-gray-500">Kategori:</span>
            <select
              value={filterCategory}
              onChange={handleCategoryChange}
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">Semua</option>
              <option value="Perawatan Rutin">Perawatan Rutin</option>
              <option value="Rem & Suspensi">Rem & Suspensi</option>
              <option value="AC & Electrical">AC & Electrical</option>
              <option value="Mesin">Mesin</option>
            </select>
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="mb-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow-md">
            <FiSettings size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">Tidak ada layanan yang ditemukan</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <div key={service.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="h-48 overflow-hidden">
                  {service.image_url ? (
                    <img
                      src={debugImageUrl(service.id, service.image_url)}
                      alt={service.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error(`Failed to load image for service ${service.id}:`, service.image_url);
                        e.target.onerror = null;
                        e.target.src = getPlaceholderUrl('No+Image', 400, 300);
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <p className="text-gray-400">No Image Available</p>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-semibold text-secondary">{service.name}</h3>
                    {getStatusBadge(service.in_stock)}
                  </div>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{service.description}</p>
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center text-gray-700">
                      <FiClock className="mr-1" />
                      <span className="text-sm">{service.estimated_time || 0} menit</span>
                    </div>
                    <div className="text-primary font-semibold">
                      <span>{formatPrice(service.price || 0)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm text-gray-600">Stok: {service.stock || 0}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {service.category}
                    </span>
                    <div className="flex items-center">
                      <button
                        onClick={() => navigate(`/admin/services/${service.id}`)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Detail
                      </button>
                      <button
                        onClick={() => handleEditService(service)}
                        className="text-yellow-600 hover:text-yellow-900 mr-3"
                      >
                        <FiEdit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteService(service)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!loading && services.length > 0 && renderPagination()}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          {modalType === 'delete' && renderDeleteModal()}
          {(modalType === 'add' || modalType === 'edit') && renderServiceForm()}
        </div>
      )}
    </DashboardLayout>
  );
};

export default AdminServices; 
